import React, { useEffect } from "react";
import {
  FormGroup,
  Stack,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Input,
} from "@mui/material";

// Initial state structure that should be used when creating the lowBackPainInfo object
export const initialLowBackPainInfo = {
  generalAppearance: "NAD",
  gait: "Non-antalgic",
  lumbar: {
    full: false,
    limitedFlexion: false,
    limitedExtension: false,
    limitedRotation: false,
    limitedLateralBending: false,
  },
  lightTouch: "intact bilaterally",
  deepTouch: "intact bilaterally",
  fromNerveDistribution: "L2",
  toNerveDistribution: "S1",
  leftPatella: "2+",
  rightPatella: "2+",
  achilles: "2+",
  babinskiSign: "Negative bilaterally",
  slrSpecialTest: "negative bilaterally",
  kempsSpecialTest: "negative bilaterally",
  faberSpecialTest: "negative bilaterally",
  fadirSpecialTest: "negative bilaterally",
  erectorSpinae: "non-tender bilaterally",
  iliopsoas: "non-tender bilaterally",
  quadratusLumborum: "non-tender bilaterally",
  greaterTrochanters: "non-tender bilaterally",
  sacroiliacJoint: "non-tender bilaterally",
  hipFlexionRight: "5",
  hipFlexionLeft: "5",
  kneeExtensionRight: "5",
  kneeExtensionLeft: "5",
  ankleDorsiflexionRight: "5",
  ankleDorsiflexionLeft: "5",
  greatToeExtensionRight: "5",
  greatToeExtensionLeft: "5",
  inversionRight: "5",
  inversionLeft: "5",
  eversionRight: "5",
  eversionLeft: "5",
  hipAbductionRight: "5",
  hipAbductionLeft: "5",
  hipExtensionRight: "5",
  hipExtensionLeft: "5",
  plantarFlexionRight: "5",
  plantarFlexionLeft: "5",
};

const BackPhysicalExam = ({ lowBackPainInfo, setLowBackPainInfo }) => {
  useEffect(() => {
    // Ensure the lumbar object is initialized before trying to access its properties
    if (!lowBackPainInfo.lumbar) {
      setLowBackPainInfo({
        ...lowBackPainInfo,
        lumbar: {
          full: false,
          limitedFlexion: false,
          limitedExtension: false,
          limitedRotation: false,
          limitedLateralBending: false,
        }
      });
    }
  }, []);

  useEffect(() => {
    if (lowBackPainInfo.deepTouch === "intact bilaterally") {
      setLowBackPainInfo({
        ...lowBackPainInfo,
        fromNerveDistribution: "L2",
        toNerveDistribution: "S1",
      });
    }
  }, [lowBackPainInfo.deepTouch]);

  const handleLumbarChange = (event) => {
    const { name, checked } = event.target;
    if (name === "full" && checked)
      setLowBackPainInfo({
        ...lowBackPainInfo,
        lumbar: {
          full: true,
          limitedFlexion: false,
          limitedExtension: false,
          limitedRotation: false,
          limitedLateralBending: false,
        },
      });
    else
      setLowBackPainInfo({
        ...lowBackPainInfo,
        lumbar: {
          ...lowBackPainInfo.lumbar,
          full: false,
          [name]: checked,
        },
      });
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={5}>
        <RadioGroup
          sx={{ width: "30%" }}
          value={lowBackPainInfo.generalAppearance || "NAD"}
          onChange={(e) =>
            setLowBackPainInfo({
              ...lowBackPainInfo,
              generalAppearance: e.target.value,
            })
          }
        >
          <FormControlLabel value="NAD" control={<Radio />} label="NAD" />
          <FormControlLabel
            value="In acute distress"
            control={<Radio />}
            label="In Acute Distress"
          />
        </RadioGroup>
        <Stack sx={{ width: "70%" }}>
          <Typography variant="h5" style={{ textDecoration: "underline" }}>
            General Appearance:
          </Typography>
          <Typography variant="h6">
            {lowBackPainInfo.generalAppearance || "NAD"}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={5}>
        <RadioGroup
          sx={{ width: "30%" }}
          value={lowBackPainInfo.gait || "Non-antalgic"}
          onChange={(e) =>
            setLowBackPainInfo({ ...lowBackPainInfo, gait: e.target.value })
          }
        >
          <FormControlLabel
            value="Non-antalgic"
            control={<Radio />}
            label="Non-antalgic"
          />
          <FormControlLabel
            value="antalgic"
            control={<Radio />}
            label="Antalgic"
          />
        </RadioGroup>
        <Stack sx={{ width: "70%" }}>
          <Typography variant="h5" style={{ textDecoration: "underline" }}>
            Gait:
          </Typography>
          <Typography variant="h6">{lowBackPainInfo.gait || "Non-antalgic"}</Typography>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={5}>
        <Stack sx={{ width: "30%" }}>
          <FormControl>
            <Typography variant="h6">Lumbar</Typography>
            <FormControl component="fieldset" variant="standard">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={lowBackPainInfo.lumbar?.full || false}
                      name="full"
                      onChange={handleLumbarChange}
                    />
                  }
                  label="Full"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={lowBackPainInfo.lumbar?.limitedFlexion || false}
                      name="limitedFlexion"
                      onChange={handleLumbarChange}
                    />
                  }
                  label="Limited Flexion"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={lowBackPainInfo.lumbar?.limitedExtension || false}
                      name="limitedExtension"
                      onChange={handleLumbarChange}
                    />
                  }
                  label="Limited Extension"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={lowBackPainInfo.lumbar?.limitedRotation || false}
                      name="limitedRotation"
                      onChange={handleLumbarChange}
                    />
                  }
                  label="Limited Rotation"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={lowBackPainInfo.lumbar?.limitedLateralBending || false}
                      name="limitedLateralBending"
                      onChange={handleLumbarChange}
                    />
                  }
                  label="Limited Lateral Bending"
                />
              </FormGroup>
            </FormControl>
          </FormControl>
        </Stack>
        <Stack sx={{ width: "70%" }}>
          <Typography variant="h5" style={{ textDecoration: "underline" }}>
            Rom:
          </Typography>
          {lowBackPainInfo.lumbar?.full && (
            <Typography variant="h6">Lumbar: Full</Typography>
          )}
          {lowBackPainInfo.lumbar?.limitedFlexion && (
            <Typography variant="h6">Lumbar: Limited Flexion</Typography>
          )}
          {lowBackPainInfo.lumbar?.limitedExtension && (
            <Typography variant="h6">Lumbar: Limited Extension</Typography>
          )}
          {lowBackPainInfo.lumbar?.limitedRotation && (
            <Typography variant="h6">Lumbar: Limited Rotation</Typography>
          )}
          {lowBackPainInfo.lumbar?.limitedLateralBending && (
            <Typography variant="h6">Lumbar: Lateral Bending</Typography>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" spacing={5}>
        <Stack sx={{ width: "30%" }} spacing={3}>
          <FormControl>
            <Typography variant="h6">Light touch </Typography>
            <RadioGroup
              value={lowBackPainInfo.lightTouch || "intact bilaterally"}
              onChange={(e) =>
                setLowBackPainInfo({
                  ...lowBackPainInfo,
                  lightTouch: e.target.value,
                })
              }
            >
              <FormControlLabel
                value={"decreased on the left"}
                control={<Radio />}
                label="Decreased on the left"
              />
              <FormControlLabel
                value={"decreased on the right"}
                control={<Radio />}
                label="Decreased on the right"
              />
              <FormControlLabel
                value={"decreased bilaterally"}
                control={<Radio />}
                label="Decreased bilaterally"
              />
              <FormControlLabel
                value={"intact bilaterally"}
                control={<Radio />}
                label="Intact bilaterally"
              />
            </RadioGroup>
          </FormControl>
          <Stack direction="column">
            <FormControl>
              <Typography variant="h6">Deep touch</Typography>
              <RadioGroup
                value={lowBackPainInfo.deepTouch || "intact bilaterally"}
                onChange={(e) =>
                  setLowBackPainInfo({
                    ...lowBackPainInfo,
                    deepTouch: e.target.value,
                  })
                }
              >
                <FormControlLabel
                  value={"decreased on the left"}
                  control={<Radio />}
                  label="Decreased on the left"
                />
                <FormControlLabel
                  value={"decreased on the right"}
                  control={<Radio />}
                  label="Decreased on the right"
                />
                <FormControlLabel
                  value={"decreased bilaterally"}
                  control={<Radio />}
                  label="Decreased bilaterally"
                />
                <FormControlLabel
                  value={"intact bilaterally"}
                  control={<Radio />}
                  label="Intact bilaterally"
                />
              </RadioGroup>
            </FormControl>
            <Stack direction="row">
              <Select
                variant="standard"
                color="primary"
                size="medium"
                value={lowBackPainInfo.fromNerveDistribution || "L2"}
                onChange={(e) =>
                  setLowBackPainInfo({
                    ...lowBackPainInfo,
                    fromNerveDistribution: e.target.value,
                  })
                }
                disabled={lowBackPainInfo.deepTouch === "intact bilaterally"}
              >
                <MenuItem value="L2">L2</MenuItem>
                <MenuItem value="L3">L3</MenuItem>
                <MenuItem value="L4">L4</MenuItem>
                <MenuItem value="L5">L5</MenuItem>
                <MenuItem value="S1">S1</MenuItem>
              </Select>
              <Select
                variant="standard"
                color="primary"
                size="medium"
                value={lowBackPainInfo.toNerveDistribution || "S1"}
                onChange={(e) =>
                  setLowBackPainInfo({
                    ...lowBackPainInfo,
                    toNerveDistribution: e.target.value,
                  })
                }
                disabled={lowBackPainInfo.deepTouch === "intact bilaterally"}
                style={{ marginLeft: "20px" }}
              >
                <MenuItem value="L2">L2</MenuItem>
                <MenuItem value="L3">L3</MenuItem>
                <MenuItem value="L4">L4</MenuItem>
                <MenuItem value="L5">L5</MenuItem>
                <MenuItem value="S1">S1</MenuItem>
              </Select>
            </Stack>
          </Stack>
        </Stack>
        <Stack sx={{ width: "70%" }}>
          <Typography variant="h5" style={{ textDecoration: "underline" }}>
            Sensation:
          </Typography>
          <Typography variant="h6">
            Light touch: {lowBackPainInfo.lightTouch || "intact bilaterally"}
          </Typography>
          <Typography variant="h6">
            Deep touch:
            {` ${lowBackPainInfo.deepTouch || "intact bilaterally"} in ${lowBackPainInfo.fromNerveDistribution || "L2"}-${lowBackPainInfo.toNerveDistribution || "S1"}`}
          </Typography>
        </Stack>
      </Stack>

      {/* The rest of your component code remains the same, but with null checks added */}
      <Stack direction="row" spacing={5}>
        <Stack sx={{ width: "30%" }}>
          <FormControl>
            <Typography variant="h6">Left patella(L2-L4)</Typography>
            <RadioGroup
              value={lowBackPainInfo.leftPatella || "2+"}
              onChange={(e) =>
                setLowBackPainInfo({
                  ...lowBackPainInfo,
                  leftPatella: e.target.value,
                })
              }
              row
            >
              <FormControlLabel
                value="1+"
                control={<Radio size="small" />}
                label="1+"
              />
              <FormControlLabel
                value="2+"
                control={<Radio size="small" />}
                label="2+"
              />
              <FormControlLabel
                value="3+"
                control={<Radio size="small" />}
                label="3+"
              />
              <FormControlLabel
                value="4+"
                control={<Radio size="small" />}
                label="4+"
              />
            </RadioGroup>
          </FormControl>
          <FormControl>
            <Typography variant="h6">Right patella(L2-L4)</Typography>
            <RadioGroup
              value={lowBackPainInfo.rightPatella || "2+"}
              onChange={(e) =>
                setLowBackPainInfo({
                  ...lowBackPainInfo,
                  rightPatella: e.target.value,
                })
              }
              row
            >
              <FormControlLabel
                value="1+"
                control={<Radio size="small" />}
                label="1+"
              />
              <FormControlLabel
                value="2+"
                control={<Radio size="small" />}
                label="2+"
              />
              <FormControlLabel
                value="3+"
                control={<Radio size="small" />}
                label="3+"
              />
              <FormControlLabel
                value="4+"
                control={<Radio size="small" />}
                label="4+"
              />
            </RadioGroup>
          </FormControl>
          <FormControl>
            <Typography variant="h6">Achilles(S1)</Typography>
            <RadioGroup
              value={lowBackPainInfo.achilles || "2+"}
              onChange={(e) =>
                setLowBackPainInfo({
                  ...lowBackPainInfo,
                  achilles: e.target.value,
                })
              }
              row
            >
              <FormControlLabel
                value="1+"
                control={<Radio size="small" />}
                label="1+"
              />
              <FormControlLabel
                value="2+"
                control={<Radio size="small" />}
                label="2+"
              />
              <FormControlLabel
                value="3+"
                control={<Radio size="small" />}
                label="3+"
              />
              <FormControlLabel
                value="4+"
                control={<Radio size="small" />}
                label="4+"
              />
            </RadioGroup>
          </FormControl>
          <FormControl>
            <Typography variant="h6">Babinski Sign</Typography>
            <RadioGroup
              value={lowBackPainInfo.babinskiSign || "Negative bilaterally"}
              onChange={(e) =>
                setLowBackPainInfo({
                  ...lowBackPainInfo,
                  babinskiSign: e.target.value,
                })
              }
            >
              <FormControlLabel
                value="Positive on the left"
                control={<Radio />}
                label="Positive on the left"
              />
              <FormControlLabel
                value="Positive on the right"
                control={<Radio />}
                label="Positive on the right"
              />
              <FormControlLabel
                value="Negative bilaterally"
                control={<Radio />}
                label="Negative bilaterally"
              />
            </RadioGroup>
          </FormControl>
        </Stack>
        <Stack sx={{ width: "70%" }}>
          <Typography variant="h5" style={{ textDecoration: "underline" }}>
            Reflexes:
          </Typography>
          <Typography variant="h6">
            Left patella(L2-L4): {lowBackPainInfo.leftPatella || "2+"}
          </Typography>
          <Typography variant="h6">
            Right patella(L2-L4): {lowBackPainInfo.rightPatella || "2+"}
          </Typography>
          <Typography variant="h6">
            Achilles(S1): {lowBackPainInfo.achilles || "2+"}
          </Typography>
          <Typography variant="h6">
            Babinski Sign: {lowBackPainInfo.babinskiSign || "Negative bilaterally"}
          </Typography>
        </Stack>
      </Stack>

      {/* Continue for other sections with similar null checks */}
      <Stack direction="row" spacing={5}>
        <Stack sx={{ width: "30%" }} spacing={3}>
          <FormControl>
            <Typography variant="h6">SLR</Typography>
            <RadioGroup
              value={lowBackPainInfo.slrSpecialTest || "negative bilaterally"}
              onChange={(e) =>
                setLowBackPainInfo({
                  ...lowBackPainInfo,
                  slrSpecialTest: e.target.value,
                })
              }
            >
              <FormControlLabel
                value="positive on the left"
                control={<Radio />}
                label="positive on the left"
              />
              <FormControlLabel
                value="positive on the right"
                control={<Radio />}
                label="positive on the right"
              />
              <FormControlLabel
                value="positive bilaterally"
                control={<Radio />}
                label="positive bilaterally"
              />
              <FormControlLabel
                value="negative bilaterally"
                control={<Radio />}
                label="negative bilaterally"
              />
            </RadioGroup>
          </FormControl>
          
          {/* The same pattern for other fields... */}
          <FormControl>
            <Typography variant="h6">Kemp's</Typography>
            <RadioGroup
              value={lowBackPainInfo.kempsSpecialTest || "negative bilaterally"}
              onChange={(e) =>
                setLowBackPainInfo({
                  ...lowBackPainInfo,
                  kempsSpecialTest: e.target.value,
                })
              }
            >
              <FormControlLabel
                value="positive on the left"
                control={<Radio />}
                label="positive on the left"
              />
              <FormControlLabel
                value="positive on the right"
                control={<Radio />}
                label="positive on the right"
              />
              <FormControlLabel
                value="positive bilaterally"
                control={<Radio />}
                label="positive bilaterally"
              />
              <FormControlLabel
                value="negative bilaterally"
                control={<Radio />}
                label="negative bilaterally"
              />
            </RadioGroup>
          </FormControl>
          
          <FormControl>
            <Typography variant="h6">FABER</Typography>
            <RadioGroup
              value={lowBackPainInfo.faberSpecialTest || "negative bilaterally"}
              onChange={(e) =>
                setLowBackPainInfo({
                  ...lowBackPainInfo,
                  faberSpecialTest: e.target.value,
                })
              }
            >
              <FormControlLabel
                value="positive on the left"
                control={<Radio />}
                label="positive on the left"
              />
              <FormControlLabel
                value="positive on the right"
                control={<Radio />}
                label="positive on the right"
              />
              <FormControlLabel
                value="positive bilaterally"
                control={<Radio />}
                label="positive bilaterally"
              />
              <FormControlLabel
                value="negative bilaterally"
                control={<Radio />}
                label="negative bilaterally"
              />
            </RadioGroup>
          </FormControl>
          
          <FormControl>
            <Typography variant="h6">FADIR</Typography>
            <RadioGroup
              value={lowBackPainInfo.fadirSpecialTest || "negative bilaterally"}
              onChange={(e) =>
                setLowBackPainInfo({
                  ...lowBackPainInfo,
                  fadirSpecialTest: e.target.value,
                })
              }
            >
              <FormControlLabel
                value="positive on the left"
                control={<Radio />}
                label="positive on the left"
              />
              <FormControlLabel
                value="positive on the right"
                control={<Radio />}
                label="positive on the right"
              />
              <FormControlLabel
                value="positive bilaterally"
                control={<Radio />}
                label="positive bilaterally"
              />
              <FormControlLabel
                value="negative bilaterally"
                control={<Radio />}
                label="negative bilaterally"
              />
            </RadioGroup>
          </FormControl>
        </Stack>
        <Stack sx={{ width: "70%" }}>
          <Typography variant="h5" style={{ textDecoration: "underline" }}>
            Special Tests:
          </Typography>
          <Typography variant="h6">
            SLR: {lowBackPainInfo.slrSpecialTest || "negative bilaterally"}
          </Typography>
          <Typography variant="h6">
            Kemp's: {lowBackPainInfo.kempsSpecialTest || "negative bilaterally"}
          </Typography>
          <Typography variant="h6">
            FABER: {lowBackPainInfo.faberSpecialTest || "negative bilaterally"}
          </Typography>
          <Typography variant="h6">
            FADIR: {lowBackPainInfo.fadirSpecialTest || "negative bilaterally"}
          </Typography>
        </Stack>
      </Stack>

      {/* Continue with similar patterns for the remaining sections */}
      <Stack direction="row" spacing={5}>
        <Stack sx={{ width: "30%" }} spacing={3}>
          <FormControl>
            <Typography variant="h6">Erector Spinae:</Typography>
            <RadioGroup
              value={lowBackPainInfo.erectorSpinae || "non-tender bilaterally"}
              onChange={(e) =>
                setLowBackPainInfo({
                  ...lowBackPainInfo,
                  erectorSpinae: e.target.value,
                })
              }
            >
              <FormControlLabel
                value="tender on the left with taut muscle bellies reproducing referred pain."
                control={<Radio />}
                label="tender on the left"
              />
              <FormControlLabel
                value="tender on the right with taut muscle bellies reproducing referred pain."
                control={<Radio />}
                label="tender on the right"
              />
              <FormControlLabel
                value="tender bilaterally with taut muscle bellies reproducing referred pain."
                control={<Radio />}
                label="tender bilaterally"
              />
              <FormControlLabel
                value="non-tender bilaterally"
                control={<Radio />}
                label="non-tender bilaterally"
              />
            </RadioGroup>
          </FormControl>
          
          {/* Similar pattern for other sections... */}
          <FormControl>
            <Typography variant="h6">Iliopsoas:</Typography>
            <RadioGroup
              value={lowBackPainInfo.iliopsoas || "non-tender bilaterally"}
              onChange={(e) =>
                setLowBackPainInfo({
                  ...lowBackPainInfo,
                  iliopsoas: e.target.value,
                })
              }
            >
              <FormControlLabel
                value="tender on the left with taut muscle bellies reproducing referred pain."
                control={<Radio />}
                label="tender on the left"
              />
              <FormControlLabel
                value="tender on the right with taut muscle bellies reproducing referred pain."
                control={<Radio />}
                label="tender on the right"
              />
              <FormControlLabel
                value="tender bilaterally with taut muscle bellies reproducing referred pain."
                control={<Radio />}
                label="tender bilaterally"
              />
              <FormControlLabel
                value="non-tender bilaterally"
                control={<Radio />}
                label="non-tender bilaterally"
              />
            </RadioGroup>
          </FormControl>
          
          <FormControl>
            <Typography variant="h6">Quadratus Lumborum:</Typography>
            <RadioGroup
              value={lowBackPainInfo.quadratusLumborum || "non-tender bilaterally"}
              onChange={(e) =>
                setLowBackPainInfo({
                  ...lowBackPainInfo,
                  quadratusLumborum: e.target.value,
                })
              }
            >
              <FormControlLabel
                value="tender on the left with taut muscle bellies reproducing referred pain."
                control={<Radio />}
                label="tender on the left"
              />
              <FormControlLabel
                value="tender on the right with taut muscle bellies reproducing referred pain."
                control={<Radio />}
                label="tender on the right"
              />
              <FormControlLabel
                value="tender bilaterally with taut muscle bellies reproducing referred pain."
                control={<Radio />}
                label="tender bilaterally"
              />
              <FormControlLabel
                value="non-tender bilaterally"
                control={<Radio />}
                label="non-tender bilaterally"
              />
            </RadioGroup>
          </FormControl>
          
          <FormControl>
            <Typography variant="h6">Greater Trochanters:</Typography>
            <RadioGroup
              value={lowBackPainInfo.greaterTrochanters || "non-tender bilaterally"}
              onChange={(e) =>
                setLowBackPainInfo({
                  ...lowBackPainInfo,
                  greaterTrochanters: e.target.value,
                })
              }
            >
              <FormControlLabel
                value="tender on the left"
                control={<Radio />}
                label="tender on the left"
              />
              <FormControlLabel
                value="tender on the right"
                control={<Radio />}
                label="tender on the right"
              />
              <FormControlLabel
                value="tender bilaterally"
                control={<Radio />}
                label="tender bilaterally"
              />
              <FormControlLabel
                value="non-tender bilaterally"
                control={<Radio />}
                label="non-tender bilaterally"
              />
            </RadioGroup>
          </FormControl>
          
          <FormControl>
            <Typography variant="h6">Sacroiliac Joint:</Typography>
            <RadioGroup
              value={lowBackPainInfo.sacroiliacJoint || "non-tender bilaterally"}
              onChange={(e) =>
                setLowBackPainInfo({
                  ...lowBackPainInfo,
                  sacroiliacJoint: e.target.value,
                })
              }
            >
              <FormControlLabel
                value="tender on the left"
                control={<Radio />}
                label="tender on the left"
              />
              <FormControlLabel
                value="tender on the right"
                control={<Radio />}
                label="tender on the right"
              />
              <FormControlLabel
                value="tender bilaterally"
                control={<Radio />}
                label="tender bilaterally"
              />
              <FormControlLabel
                value="non-tender bilaterally"
                control={<Radio />}
                label="non-tender bilaterally"
              />
            </RadioGroup>
          </FormControl>
        </Stack>
        <Stack sx={{ width: "70%" }}>
          <Typography variant="h5" style={{ textDecoration: "underline" }}>
            Palpation:
          </Typography>
          <Typography variant="h6">
            Erector Spinae: {lowBackPainInfo.erectorSpinae || "non-tender bilaterally"}
          </Typography>
          <Typography variant="h6">
            Iliopsoas: {lowBackPainInfo.iliopsoas || "non-tender bilaterally"}
          </Typography>
          <Typography variant="h6">
            Quadratus Lumborum: {lowBackPainInfo.quadratusLumborum || "non-tender bilaterally"}
          </Typography>
          <Typography variant="h6">
            Greater Trochanters: {lowBackPainInfo.greaterTrochanters || "non-tender bilaterally"}
          </Typography>
          <Typography variant="h6">
            Sacroiliac Joint: {lowBackPainInfo.sacroiliacJoint || "non-tender bilaterally"}
          </Typography>
        </Stack>
      </Stack>
      
      <div style={{ marginLeft: "300px" }}>
        <Typography variant="h5" style={{ textDecoration: "underline" }}>
          Motor:
        </Typography>
        <TableContainer>
          <Table style={{ width: "500px" }}>
            <TableBody>
              <TableRow key="1">
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell
                  style={{ fontWeight: "bold", textDecoration: "underline" }}
                >
                  Right
                </TableCell>
                <TableCell
                  style={{ fontWeight: "bold", textDecoration: "underline" }}
                >
                  Left
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </Stack>
  );
};

export default BackPhysicalExam;
